import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  Label,
  Input,
  Textarea,
} from "@donex/ui";
import { config } from "@/lib/config";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type Status = "idle" | "loading" | "success" | "error";

export function FeedbackModal({ open, onOpenChange }: Props) {
  const [what, setWhat] = useState("");
  const [steps, setSteps] = useState("");
  const [status, setStatus] = useState<Status>("idle");

  const reset = () => {
    setWhat("");
    setSteps("");
    setStatus("idle");
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const res = await fetch(`${config.apiBaseUrl}/feedback`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", Authorization: "cookie" },
        body: JSON.stringify({ what, steps: steps || undefined }),
      });
      if (!res.ok) throw new Error("Failed");
      setStatus("success");
      setTimeout(() => handleOpenChange(false), 1200);
    } catch {
      setStatus("error");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send feedback</DialogTitle>
        </DialogHeader>

        {status === "success" ? (
          <p className="py-4 text-center text-sm text-muted-foreground">Thanks — we got it! ✓</p>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="fb-what">What happened?</Label>
              <Input
                id="fb-what"
                value={what}
                onChange={(e) => setWhat(e.target.value)}
                placeholder="Describe the issue or suggestion…"
                required
                disabled={status === "loading"}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="fb-steps">
                Steps to reproduce{" "}
                <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Textarea
                id="fb-steps"
                value={steps}
                onChange={(e) => setSteps(e.target.value)}
                placeholder="1. Go to…&#10;2. Click on…&#10;3. See error"
                rows={4}
                disabled={status === "loading"}
              />
            </div>

            {status === "error" && (
              <p className="text-sm text-destructive">Something went wrong — please try again.</p>
            )}

            <Button type="submit" disabled={status === "loading"} className="w-full">
              {status === "loading" ? "Sending…" : "Send feedback"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
